import { useState, useEffect, useCallback, useRef } from "react";
import { Search, FileText, Loader2, BookOpen } from "lucide-react";
import api from "../../lib/api";
import { Button } from "../../components/ui/button";
import { previewPdfRequest } from "../../lib/pdfPreview";

/**
 * Jun-2026 (User-Wunsch) — SKR51-Kontenplan (Branchenpaket Kfz).
 * Durchsuchbare Kontenliste (Backend-Suche) + Klassen-Filter + eingebettete
 * Ansicht der Original-DATEV-PDF.
 */
export default function SKR51KontenplanTab() {
    const [q, setQ] = useState("");
    const [klasse, setKlasse] = useState("");
    const [klassen, setKlassen] = useState({});
    const [accounts, setAccounts] = useState([]);
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(false);
    const timer = useRef(null);

    const load = useCallback(async (term, kl) => {
        setLoading(true);
        try {
            const r = await api.get(`/buchhaltung/skr51/accounts`, { params: { q: term, klasse: kl, limit: 400 } });
            setAccounts(r.data?.accounts || []);
            setTotal(r.data?.total || 0);
            if (r.data?.klassen) setKlassen(r.data.klassen);
        } catch { setAccounts([]); }
        finally { setLoading(false); }
    }, []);

    useEffect(() => {
        if (timer.current) clearTimeout(timer.current);
        timer.current = setTimeout(() => load(q, klasse), 250);
        return () => timer.current && clearTimeout(timer.current);
    }, [q, klasse, load]);

    // PDF wird mit Auth-Header geladen → in der globalen Canvas-Vorschau anzeigen
    const openPdf = useCallback(() => {
        previewPdfRequest(
            api.get(`/buchhaltung/skr51/pdf`, { responseType: "blob" }),
            "SKR51-Kontenplan.pdf",
        );
    }, []);

    return (
        <div className="space-y-4" data-testid="skr51-kontenplan-tab">
            <div className="bg-[#111827] text-white rounded-sm p-5">
                <div className="flex items-center gap-2 mb-1">
                    <BookOpen className="w-5 h-5 text-[#FFCC00]" />
                    <h2 className="font-display font-black text-lg">SKR51-Kontenplan</h2>
                </div>
                <p className="text-xs text-gray-300">DATEV-Branchenpaket Kfz (SKR 51) — durchsuchbare Kontenliste &amp; Original-PDF.</p>
            </div>

            {/* Suchleiste + Aktionen */}
            <div className="flex flex-col sm:flex-row gap-3 items-stretch sm:items-center">
                <div className="relative flex-1">
                    <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
                    <input
                        value={q}
                        onChange={(e) => setQ(e.target.value)}
                        placeholder="Konto-Nr. oder Bezeichnung suchen (z. B. 8400, Erlöse Werkstatt, Vorsteuer)…"
                        className="w-full h-11 rounded-sm border border-gray-300 pl-9 pr-3 text-sm"
                        data-testid="skr51-search"
                    />
                </div>
                <Button onClick={openPdf} variant="outline" className="rounded-sm h-11 gap-2" data-testid="skr51-view-pdf">
                    <FileText className="w-4 h-4" /> PDF ansehen
                </Button>
            </div>

            {/* Klassen-Filter */}
            <div className="flex flex-wrap gap-1.5" data-testid="skr51-class-filter">
                <button type="button" onClick={() => setKlasse("")}
                    className={`text-xs font-bold px-2.5 py-1 rounded-sm border ${klasse === "" ? "bg-[#111827] text-white border-[#111827]" : "bg-white text-gray-600 border-gray-300 hover:border-gray-400"}`}
                    data-testid="skr51-class-all">Alle</button>
                {Object.entries(klassen).map(([k, lbl]) => (
                    <button key={k} type="button" onClick={() => setKlasse(k)} title={lbl}
                        className={`text-xs font-bold px-2.5 py-1 rounded-sm border ${klasse === k ? "bg-[#FFCC00] text-[#111827] border-[#FFCC00]" : "bg-white text-gray-600 border-gray-300 hover:border-gray-400"}`}
                        data-testid={`skr51-class-${k}`}>{k}</button>
                ))}
            </div>

            {/* Ergebnis */}
            <div className="text-xs text-gray-500" data-testid="skr51-count">
                {loading ? <span className="inline-flex items-center gap-1"><Loader2 className="w-3 h-3 animate-spin" /> Lädt…</span> : `${total} Konten`}
            </div>

            <div className="bg-white border border-gray-200 rounded-sm overflow-hidden">
                <table className="w-full text-sm">
                    <thead className="bg-gray-50 border-b border-gray-200">
                        <tr>
                            <th className="text-left font-bold text-[10px] uppercase tracking-wider text-gray-500 px-4 py-2 w-24">Konto</th>
                            <th className="text-left font-bold text-[10px] uppercase tracking-wider text-gray-500 px-4 py-2">Bezeichnung</th>
                            <th className="text-left font-bold text-[10px] uppercase tracking-wider text-gray-500 px-4 py-2 w-16">Klasse</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100" data-testid="skr51-list">
                        {accounts.map((a) => (
                            <tr key={a.konto} className="hover:bg-gray-50" data-testid={`skr51-row-${a.konto}`}>
                                <td className="px-4 py-2 font-mono-num font-bold text-[#111827]">{a.konto}</td>
                                <td className="px-4 py-2 text-gray-700">{a.bezeichnung}</td>
                                <td className="px-4 py-2"><span className="text-[10px] font-bold bg-gray-100 text-gray-500 px-1.5 py-0.5 rounded-sm" title={a.klasse_label}>{a.klasse}</span></td>
                            </tr>
                        ))}
                        {!loading && accounts.length === 0 && (
                            <tr><td colSpan={3} className="px-4 py-8 text-center text-gray-400" data-testid="skr51-empty">Keine Konten gefunden.</td></tr>
                        )}
                    </tbody>
                </table>
            </div>
        </div>
    );
}
